import Page from '@/components/page/Page.jsx';
import Panel from '@/components/panel/Panel.jsx';
import { FiEdit2 } from '@/components/react-icons/main';
import selectors from '@/redux/selectors/selectors.js';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import CardOutput from './CardOutput';
import DataViewerOutput from './DataViewerOutput';
import IdOutput from './IdOutput';
import { useAnalytics } from '@/hooks/useAnalytics.js';
import { action } from '@/lib/api';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import runActiveWorkflowScreenStep from '@/screens/workflows/redux/actions/runActiveWorkflowScreenStep';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import getParentIterationStepIds from '@/screens/workflows/lib/getParentIterationStepIds';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import { getWorkflowResponses } from '@/redux/actions/gravityActions';
import { getAllGraphStepChildrenIds } from '@shared/graph/getAllGraphStepChildrenIds.js';
import setQuery from '@/lib/setQuery.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import setPath from '@/lib/setPath';

const OutputObject = ({ output, index, numberingStartIndex, singleton, isSelected, selectableIndex, search }) => {
  const [showDetails, setShowDetails] = useState(index === numberingStartIndex);
  const presentation = output?.__meta?.presentation || {};
  const fields = presentation?.fields || {};
  const fieldsAreEmpty = JSON.stringify(fields) === '{}';
  const presentationType = !fields.name ? 'id' : presentation?.type;
  const hidePreviews = Object.keys(output || {})?.length < 3;

  const handleClick = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div className={`output-object`}>
      <div className={'output-contents'}>
        {presentationType === 'card' && (
          <CardOutput onClick={handleClick} output={output} active={showDetails} index={index} isSelected={isSelected} selectableIndex={selectableIndex} hidePreviews={hidePreviews}>
            {showDetails && <DataViewerOutput obj={output} singleton={singleton} expanded={!presentationType} search={search} />}
          </CardOutput>
        )}
        {['none', 'id', undefined].includes(presentationType) && (
          <IdOutput onClick={handleClick} output={output} active={showDetails} index={index} isSelected={isSelected} selectableIndex={selectableIndex} hidePreviews={hidePreviews}>
            {(showDetails || fieldsAreEmpty || hidePreviews) && <DataViewerOutput obj={output} singleton={singleton} expanded={!presentationType} forceTruncated={false} search={search} />}
          </IdOutput>
        )}
      </div>
    </div>
  );
};

const OutputObjects = ({ search, output, numberingStartIndex, activeIterationIndex, selectableIndex }) => {
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const workflowVersion = useArgSelector(selectors.param, 'workflowVersion');
  const stepId = useArgSelector(selectors.query, 'stepId') ?? useArgSelector(selectors.query, 'logStepId');
  const querySearch = useArgSelector(selectors.query, 'search');
  const actionOperation = useSelector((state) => workflowScreenSelectors.activeWorkflowStepAppAction(state)?.operation);
  const steps = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.steps);
  const apps = useSelector(selectors.appsMetadata);
  const graph = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.graph);
  const pathname = useSelector((state) => state?.router?.location?.pathname);
  const { trackEvent } = useAnalytics();

  const parentIterationStepIds = getParentIterationStepIds(stepId, steps, apps, graph);
  const parentStep = steps?.find((n) => parentIterationStepIds?.[0] === n?.id);
  const isInLoop = parentStep?.appSlug === 'iterate';
  const isAppScreen = pathname?.includes('/apps');

  const showTryAgainNextLoop = actionOperation === 'read' && output?.length === 0 && isInLoop;
  const activeParentLoopIndex = parentStep?.arguments?.activeIndex;

  if (!Array.isArray(output)) {
    return null;
  }

  const handleUpdateStepClick = () => {
    setPath(`/workflows/${workflowId}/${workflowVersion}?stepId=${stepId}&stepTab=config`);
    setModalState('workflowLogs', { open: false });
    setModalState('logStepOutput', { open: false });
  };

  const handleRunClick = async () => {
    action('SET_STEP_STATE', { [stepId]: { status: 'starting' } });
    const childStepIds = getAllGraphStepChildrenIds(graph, parentStep?.id);
    await stepActions.set('arguments.activeIndex', activeParentLoopIndex + 1, { stepId: parentStep?.id });
    await getWorkflowResponses({ workflowId, stepIds: childStepIds, search: querySearch });
    await waitForSyncReady();
    trackEvent('workflow-editor-runStep');
    runActiveWorkflowScreenStep(() => {
      action('SET_STEP_STATE', { [stepId]: null });
      action('SET_WORKFLOW_PANEL_EXPANDED', false);
      if (window.location.pathname?.includes(stepId)) {
        setQuery({ stepTab: 'output', outputType: 'live' });
      }
    });
  };

  const actions = search ? [] : isAppScreen ? [] : [{ label: 'Update Step Settings', icon: FiEdit2, onClick: handleUpdateStepClick, size: 'sm', className: 'primary' }];

  if (showTryAgainNextLoop) {
    actions.push({
      label: `Try Again on Loop ${activeParentLoopIndex + 2}`,
      onClick: handleRunClick,
      size: 'sm',
      className: 'secondary',
      dataTitle: `No results found in Loop ${activeParentLoopIndex + 1}. Click to run Loop ${activeParentLoopIndex + 2} and potentially get results.`
    });
  }

  const emptyStateTitle = search ? 'No results found' : 'No results';
  const emptyStateDescription = search ? 'Try adjusting your search query.' : `The ${isAppScreen ? 'action' : 'step'} succeeded, but returned no data.`;

  if (output?.length === 0) {
    return (
      <Page.Section variant={'chromeless'} className={'full-height output-empty-state'}>
        <Panel.EmptyState title={emptyStateTitle} description={emptyStateDescription} actions={actions} />
      </Page.Section>
    );
  }

  return (
    <div className={'output-objects'}>
      {output?.map((obj, idx) => (
        <OutputObject //
          output={obj}
          key={idx}
          index={numberingStartIndex + idx}
          numberingStartIndex={numberingStartIndex}
          singleton={output?.length === 1}
          isSelected={numberingStartIndex + idx === activeIterationIndex}
          selectableIndex={selectableIndex}
          search={search}
        />
      ))}
    </div>
  );
};

export default OutputObjects;
